import React, { useEffect, useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getApiUrl } from "../hooks/useAuth";
import { CompressImageFile } from "../components/CompressImageFile";
import { ToastContainer, toast } from "react-toastify";
import { useAuth } from '../hooks/useAuth';
function DoseReportsList() {
     // Move ALL hooks to the top - before any conditional logic
     const [doseReports, setDoseReports] = useState([]);
     const [loading, setLoading] = useState(true);
     const [selectedReport, setSelectedReport] = useState(null);
     const [error, setError] = useState(null);
     const [branches, setBranches] = useState([]);
     const year = new Date().getFullYear();
     const [attachments, setAttachments] = useState([]);
     const attachmentsInputRef = useRef(null);
     const [filterBranch, setFilterBranch] = useState("");
     const [filterYear, setFilterYear] = useState(year);
     const [filterQuarter, setFilterQuarter] = useState("");
     const [showModal, setShowModal] = useState(false);
     const [newReportData, setNewReportData] = useState({
          branch_id: "",
          cycle_period: "",
          quarter: "",
          year: year,
          attachments: [],
     });
     const { user } = useAuth();

     const quarterToPeriod = {
          Q1: "Jan–Mar",
          Q2: "Apr–Jun",
          Q3: "Jul–Sep",
          Q4: "Oct–Dec",
     };
     const quarters = [
          { value: "Q1", label: "Jan–Mar" },
          { value: "Q2", label: "Apr–Jun" },
          { value: "Q3", label: "Jul–Sep" },
          { value: "Q4", label: "Oct–Dec" },
     ];

     const token = sessionStorage.getItem("token") || localStorage.getItem("token");

     const handleSubmitNewReport = (e) => {
          e.preventDefault();
          // Validate required fields
          if (!newReportData.branch_id || !newReportData.quarter) {
               toast.error("Please fill in all required fields.");
               return;
          }
          if (attachments.length === 0) {
               toast.error("Please attach at least one file.");
               return;
          }
          const formData = new FormData();
          formData.append("data", JSON.stringify({
               branch_id: newReportData.branch_id,
               quarter: newReportData.quarter,
               year: year,
               cycle_period: quarterToPeriod[newReportData.quarter],
          }));
          attachments.forEach((att) => {
               formData.append("files[]", att.file);
          });

          const token = sessionStorage.getItem("token") || localStorage.getItem("token");
          fetch(getApiUrl("/createDoseReport"), {
               method: "POST",
               headers: {
                    Authorization: `Bearer ${token}`,
               },
               body: formData,
          })
               .then((res) => res.json())
               .then((data) => {
                    if (data.status) { // <-- check for status
                         toast.success("Dose report added successfully.");
                         setShowModal(false);
                         setNewReportData({ branch_id: "", quarter: "", year, attachments: [] });
                         setAttachments([]);
                         // Optionally refresh list here
                         fetchAllDoseReports();
                    } else {
                         toast.error(data.message || "Failed to add dose report.");
                    }
               })
               .catch((err) => {
                    toast.error("Failed to add dose report.");
               });
     };

     const handleFileChange = async (e) => {
          const files = Array.from(e.target.files);
          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
          const MAX_SIZE_MB = 5;

          let newAttachments = [];
          for (let file of files) {
               let message = "";
               let preview = "";
               let processedFile = file;

               if (!validTypes.includes(file.type)) {
                    alert("Only JPG, PNG or PDF allowed");
                    if (attachmentsInputRef.current) attachmentsInputRef.current.value = "";
                    return;
               } else if (file.type === "application/pdf") {
                    if (file.size / 1024 / 1024 > 5) {
                         alert("PDF exceeds 5MB. Please select a smaller file.");
                         if (attachmentsInputRef.current) attachmentsInputRef.current.value = "";
                         return;
                    }
                    // --- PDF script detection ---
                    const arrayBuffer = await file.arrayBuffer();
                    const text = new TextDecoder("utf-8").decode(arrayBuffer);
                    // Look for common script embedding keywords in PDF
                    if (/\/(JavaScript|JS|AA|OpenAction)/i.test(text)) {
                         alert("This PDF contains embedded scripts and is not allowed for VDC security policy reasons.");
                         if (attachmentsInputRef.current) attachmentsInputRef.current.value = "";
                         return;
                    }
                    // PDF icon as preview
                    preview = 'data:image/svg+xml;base64,' + btoa(`
                         <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#dc3545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                         <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                         <polyline points="14,2 14,8 20,8"/>
                         <text x="12" y="18" font-family="Arial, sans-serif" font-size="4" text-anchor="middle" fill="#dc3545">PDF</text>
                         </svg>
                         `);
               } else if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
                    // Compress image if too large
                    try {
                         processedFile = await CompressImageFile(file, MAX_SIZE_MB, "Attachment");
                         if (processedFile.size / 1024 / 1024 > MAX_SIZE_MB) {
                              alert("Image still exceeds 5MB after compression");
                              if (attachmentsInputRef.current) attachmentsInputRef.current.value = "";
                              return;
                         }
                    } catch (err) {
                         alert("Image compression failed");
                         if (attachmentsInputRef.current) attachmentsInputRef.current.value = "";
                         return;
                    }
                    preview = URL.createObjectURL(processedFile);
               } else {
                    preview = URL.createObjectURL(processedFile);
               }
               newAttachments.push({
                    file: processedFile,
                    preview,
                    type: file.type,
                    message,
               });
          }
          setAttachments(prev => [...prev, ...newAttachments]);
          // Optionally clear input value so same file can be re-added
          if (attachmentsInputRef.current) attachmentsInputRef.current.value = "";
     };

     const removeAttachment = (idx) => {
          setAttachments(prev => {
               // Clean up object URL if image
               const att = prev[idx];
               if (att && att.preview && att.type !== "application/pdf") {
                    try { URL.revokeObjectURL(att.preview); } catch { }
               }
               return prev.filter((_, i) => i !== idx);
          });
     };

     // Fetch all branches
     useEffect(() => {
          if (!token) {
               setError("You are not authenticated. Please log in.");
               setLoading(false);
               return;
          }
          fetch(getApiUrl("/getBranches"), {
               headers: {
                    Authorization: `Bearer ${token}`,

               },
          })
               .then((res) => res.json())
               .then((data) => {
                    setBranches(Array.isArray(data.data) ? data.data : []);
               })
               .catch((err) => {
                    setError("Failed to fetch branches");
               });
     }, [token]);

     // Fetch all dose reports
     const fetchAllDoseReports = useCallback(() => {
          if (!token) {
               setError("You are not authenticated. Please log in.");
               setLoading(false);
               return;
          }
          fetch(getApiUrl("/getDoseReports"), {
               headers: {
                    Authorization: `Bearer ${token}`,

               },
          })
               .then((res) => res.json())
               .then((data) => {
                    setDoseReports(Array.isArray(data.data) ? data.data : []);
                    setLoading(false);
               })
               .catch((err) => {
                    setError("Failed to fetch dose reports");
                    setLoading(false);
               });
     }, [token]);

     useEffect(() => {
          fetchAllDoseReports();
     }, [token, fetchAllDoseReports]);

     // Access check - move this AFTER all hooks
     const allowedRoles = ['ADMIN', 'QUALITY_ADMIN', 'SUPER_ADMIN'];
     const hasAccess = allowedRoles.includes(user?.role);

     console.log('Should have access:', hasAccess);
     console.log('Allowed roles:', allowedRoles);
     console.log('User role:', user?.role);

     // Return access denied if no access - this is fine after all hooks
     if (!hasAccess) {
          return <div className="alert alert-danger">Access Denied: You don't have permission to view this page.</div>;
     }

     // Download as Excel
     const handleDownloadExcel = () => {
          if (!doseReports || doseReports.length === 0) {
               toast.error("No data to export.");
               return;
          }
          // Prepare data for Excel
          const exportData = doseReports.map((r) => ({
               "Report ID": r.report_id,
               "Branch": r.branch_name,
               "Year": r.year,
               "Cycle Period": r.cycle_period,
               "Uploaded By": r.uploaded_by,
               "Created At": r.created_at,
          }));
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "DoseReports");
          const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
          const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
          saveAs(blob, "dose_reports.xlsx");
     };

     if (loading) return <div>Loading...</div>;
     if (error) return <div style={{ color: "red" }}>{error}</div>;

     return <div style={{ padding: "5px" }}>
          <div className="d-flex justify-content-between align-items-left">
               <h2 className="h4">Dose Reports List</h2>
               <div className="col-auto d-flex align-items-center">
                    <button
                         onClick={handleDownloadExcel}
                         className="btn btn-success btn-sm"
                         type="button"
                    >
                         <i className="fas fa-file-excel mr-1"></i> Download Excel
                    </button>
                    <button
                         onClick={() => setShowModal(true)}
                         className="btn btn-primary btn-sm ml-2"
                         type="button"
                    >
                         <i className="fas fa-plus"></i> New Report
                    </button>
               </div>
          </div>
          <ToastContainer position="top-right" />
          <div className="card shadow mt-2 mb-4">
               <div className="card-header">
                    <div className="row ">
                         <div className="col-md-3">
                              <select
                                   className="form-control form-control-sm"
                                   value={filterBranch}
                                   onChange={e => setFilterBranch(e.target.value)}
                              >
                                   <option value="">All Branches</option>
                                   {branches.map(branch => (
                                        <option key={branch.branch_id} value={branch.branch_id}>
                                             {branch.branch_name}
                                        </option>
                                   ))}
                              </select>
                         </div>
                         <div className="col-md-2">
                              <input
                                   type="number"
                                   className="form-control form-control-sm"
                                   value={filterYear}
                                   onChange={e => setFilterYear(e.target.value)}
                                   placeholder="Year"
                                   min="2000"
                                   max="2100"
                              />
                         </div>
                         <div className="col-md-3">
                              <select
                                   className="form-control form-control-sm"
                                   value={filterQuarter}
                                   onChange={e => setFilterQuarter(e.target.value)}
                              >
                                   <option value="">All Periods</option>
                                   {quarters.map(q => (
                                        <option key={q.value} value={q.value}>{q.label}</option>
                                   ))}
                              </select>
                         </div>
                    </div>
               </div>
               <div className="card-body ">
                    <div style={{ overflowX: "auto", }}>
                         <table className="table table-bordered table-striped">
                              <thead>
                                   <tr>
                                        <th>S.No</th>
                                        <th>Branch </th>
                                        <th>Year</th>
                                        <th>Cycle Period</th>
                                        <th>Actions</th>
                                   </tr>
                              </thead>
                              <tbody>
                                   {doseReports.length === 0 ? (
                                        <tr>
                                             <td colSpan={5} style={{ textAlign: "center" }}>
                                                  No reports found.
                                             </td>
                                        </tr>
                                   ) : (
                                        doseReports
                                             .filter(report =>
                                                  (!filterBranch || report.branch_id === filterBranch) &&
                                                  (!filterYear || String(report.year) === String(filterYear)) &&
                                                  (!filterQuarter || report.quarter === filterQuarter)
                                             )
                                             .map((report, index) => (
                                                  <tr key={report.id}>
                                                       <td>{index + 1}</td>
                                                       <td>{report.branch_name}</td>
                                                       <td>{report.year}</td>
                                                       <td>{report.cycle_period}</td>
                                                       <td>
                                                            {report.file_path && (
                                                                 <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                                      {/* Preview */}
                                                                      {/\.(jpe?g|png)$/i.test(report.file_path) ? (
                                                                           <img
                                                                                src={
                                                                                     report.file_path.startsWith('http')
                                                                                          ? report.file_path
                                                                                          : `${process.env.REACT_APP_BASE_URL}/${encodeURI(report.file_path.replace(/^\/+/, ''))}`
                                                                                }
                                                                                alt="Dose Report"
                                                                                style={{
                                                                                     width: 20,
                                                                                     height: 20,
                                                                                     objectFit: "cover",
                                                                                     border: "1px solid #ccc",
                                                                                     borderRadius: 4,
                                                                                }}
                                                                           />
                                                                      ) : report.file_path.toLowerCase().endsWith('.pdf') ? (
                                                                           <img
                                                                                src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzNiIgaGVpZ2h0PSIzNiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNkYzM1NDUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMTQgMkg2YTIgMiAwIDAgMC0yIDJ2MTZhMiAyIDAgMCAwIDIgMmgxMmEyIDIgMCAwIDAgMi0yVjhoLTZ6Ii8+PHBvbHlsaW5lIHBvaW50cz0iMTQsMiAxNCw4IDIwLDgiLz48dGV4dCB4PSIxMiIgeT0iMTgiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSI0IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjZGMzNTQ1Ij5QREY8L3RleHQ+PC9zdmc+"
                                                                                alt="Dose Report PDF"
                                                                                style={{
                                                                                     width: 20,
                                                                                     height: 20,
                                                                                     border: "1px solid #ccc",
                                                                                     borderRadius: 4,
                                                                                     objectFit: "cover"
                                                                                }}
                                                                           />
                                                                      ) : null}
                                                                      {/* View link */}
                                                                      <a
                                                                           href={
                                                                                report.file_path.startsWith('http')
                                                                                     ? report.file_path
                                                                                     : `${process.env.REACT_APP_BASE_URL}/${encodeURI(report.file_path.replace(/^\/+/, ''))}`
                                                                           }
                                                                           target="_blank"
                                                                           rel="noopener noreferrer"
                                                                      >
                                                                           View Dose Report
                                                                      </a>
                                                                 </div>
                                                            )}
                                                       </td>
                                                  </tr>
                                             ))
                                   )}
                              </tbody>
                         </table>
                    </div>
                    {selectedReport && (
                         <div style={{ marginTop: 24, border: "1px solid #ccc", padding: 16, borderRadius: 8, background: "#f9f9f9" }}>
                              <div className="d-flex justify-content-between align-items-center">
                                   <h3 className="h5 mb-0">Dose Report Details</h3>
                                   <button className="btn btn-outline-secondary btn-sm" onClick={() => setSelectedReport(null)}>
                                        Close
                                   </button>
                              </div>
                              <hr />
                              <pre style={{ background: "#fff", padding: 12, borderRadius: 4 }}>
                                   {JSON.stringify(selectedReport, null, 2)}
                              </pre>
                         </div>
                    )}
               </div>
          </div>
          {showModal && (
               <div className="modal fade show" tabIndex="-1" role="dialog" style={{ display: "block", background: "rgba(0,0,0,0.4)" }}>
                    <div className="modal-dialog modal-dialog-top" role="document">
                         <div className="modal-content">
                              <div className="modal-header">
                                   <h5 className="modal-title">Add New Dose Report</h5>
                                   <button type="button" className="close" onClick={() => setShowModal(false)}>
                                        <span>&times;</span>
                                   </button>
                              </div>
                              <div className="modal-body">
                                   <form onSubmit={handleSubmitNewReport}>
                                        <div className="form-group row align-items-center">
                                             <label className="col-sm-4 col-form-label text-left">Branch</label>
                                             <div className="col-sm-8">
                                                  <select
                                                       className="form-control form-control-sm"
                                                       value={newReportData.branch_id}
                                                       onChange={(e) => setNewReportData({ ...newReportData, branch_id: e.target.value })}
                                                  >
                                                       <option value="">Select Branch</option>
                                                       {branches.map((branch) => (
                                                            <option key={branch.branch_id} value={branch.branch_id}>
                                                                 {branch.branch_name}
                                                            </option>
                                                       ))}
                                                  </select>
                                             </div>
                                        </div>

                                        <div className="form-group row align-items-center">
                                             <label className="col-sm-4 col-form-label text-left">Year</label>
                                             <div className="col-sm-8">
                                                  <input
                                                       type="number"
                                                       className="form-control form-control-sm"
                                                       value={year}
                                                       readOnly
                                                  />
                                             </div>
                                        </div>
                                        <div className="form-group row align-items-center">
                                             <label className="col-sm-4 col-form-label text-left">Cycle Period</label>
                                             <div className="col-sm-8">
                                                  <select
                                                       className="form-control form-control-sm"
                                                       value={newReportData.quarter}
                                                       onChange={(e) => setNewReportData({ ...newReportData, quarter: e.target.value })}
                                                       required
                                                  >
                                                       <option value="">Select Period</option>
                                                       {quarters.map((q) => (
                                                            <option key={q.value} value={q.value}>
                                                                 {q.label}
                                                            </option>
                                                       ))}
                                                  </select>
                                             </div>
                                        </div>
                                        {/* file attachment */}
                                        <div className="form-group row align-items-top">
                                             <label className="col-sm-4 col-form-label text-left">File Attachment</label>
                                             <div className="col-sm-8">
                                                  <input
                                                       type="file"
                                                       className="form-control form-control-sm"
                                                       onChange={handleFileChange}
                                                       ref={attachmentsInputRef}
                                                  />

                                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                                                       {attachments.map((att, idx) => (
                                                            <div
                                                                 key={idx}
                                                                 style={{
                                                                      textAlign: "center",
                                                                      position: "relative",
                                                                      width: 60,
                                                                      minHeight: 60,
                                                                      marginBottom: 8,
                                                                 }}
                                                            >
                                                                 {att.preview && (
                                                                      att.type === "application/pdf" ? (
                                                                           <img
                                                                                src={att.preview}
                                                                                alt="PDF"
                                                                                style={{
                                                                                     width: 36,
                                                                                     height: 36,
                                                                                     border: "1px solid #dc3545",
                                                                                     borderRadius: 4,
                                                                                     marginTop: 8,
                                                                                }}
                                                                           />
                                                                      ) : (
                                                                           <img
                                                                                src={att.preview}
                                                                                alt="Preview"
                                                                                style={{
                                                                                     width: 48,
                                                                                     height: 40,
                                                                                     objectFit: "cover",
                                                                                     borderRadius: 4,
                                                                                     border: "1px solid #ddd",
                                                                                     marginTop: 8,
                                                                                }}
                                                                           />
                                                                      )
                                                                 )}
                                                                 {/* Remove button - absolute top right */}
                                                                 <button
                                                                      type="button"
                                                                      onClick={() => removeAttachment(idx)}
                                                                      style={{
                                                                           position: "absolute",
                                                                           top: 0,
                                                                           right: 0,
                                                                           background: "#fff",
                                                                           border: "1px solid #dc3545",
                                                                           color: "#dc3545",
                                                                           borderRadius: "50%",
                                                                           width: 20,
                                                                           height: 20,
                                                                           fontSize: 12,
                                                                           cursor: "pointer",
                                                                           lineHeight: "18px",
                                                                           padding: 0,
                                                                           zIndex: 2,
                                                                      }}
                                                                      title="Remove"
                                                                 >×</button>
                                                                 <div style={{ fontSize: "0.7em", marginTop: 2, maxWidth: 80, wordBreak: "break-all" }}>
                                                                      {att.file?.name && att.file.name.length > 18
                                                                           ? `${att.file.name.slice(0, 8)}...${att.file.name.slice(-8)}`
                                                                           : att.file?.name}
                                                                 </div>
                                                                 {att.message && (
                                                                      <div style={{ color: "red", fontSize: "0.7em" }}>{att.message}</div>
                                                                 )}
                                                            </div>
                                                       ))}
                                                  </div>
                                             </div>
                                        </div>
                                        <div className="form-group row align-items-center">
                                             {/* submit button */}
                                             <div className="col-sm-8 offset-sm-4">
                                                  <button type="submit" className="btn btn-primary">
                                                       Submit
                                                  </button>
                                             </div>
                                        </div>
                                   </form>
                              </div>
                         </div>
                    </div>
               </div >
          )
          }
     </div >;
}



export default DoseReportsList;